import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import "./FirstStageConfirm.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LoginIcon from "@mui/icons-material/Login";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";

const LOGIN_URL = "https://registration.wedochallenge.org/login";

const STEPS = [
  {
    title: "Log in to your account",
    description:
      'Click the "Log in to your account" button below to open the registration portal.',
  },
  {
    title: "Enter your credentials",
    description:
      "Use your Account Code and the password you created during registration.",
  },
  {
    title: "Complete your registration",
    description:
      "Add your teams, members, and coaches, then complete the payment process to finalize your registration.",
  },
];

const FirstStageConfirmTemplate = ({ application }) => {
  const [copied, setCopied] = useState(false);
  const accountCode = application?.loginData?.accountCode;

  const handleCopyCode = async () => {
    if (!accountCode) return;
    try {
      await navigator.clipboard.writeText(accountCode);
      setCopied(true);
      toast.success("Account code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Unable to copy account code.");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: "50px 20px", sm: "50px 60px", lg: "50px 120px" },
        maxWidth: "900px",
        margin: "auto",
      }}
    >
      <Box className="confirmHero">
        <Box className="successBadge">
          <CheckCircleOutlineIcon className="successIcon" />
        </Box>

        <Typography
          variant="h1"
          sx={{
            color: "primary.darker",
            fontSize: { xs: "28px", sm: "34px", md: "42px" },
            lineHeight: 1.15,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            textAlign: "center",
            marginTop: "24px",
          }}
        >
          Registration Successful!
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            fontSize: { xs: "16px", sm: "18px" },
            fontWeight: 400,
            color: "primary.darker",
            marginTop: "10px",
            maxWidth: "640px",
            marginInline: "auto",
            lineHeight: 1.6,
          }}
        >
          You&apos;ve successfully registered in our system, and we&apos;re
          excited to have you on board. We&apos;re here to support you as you
          take the next steps — don&apos;t hesitate to reach out if you need
          any help.
        </Typography>
      </Box>

      <Box className="accountCodeCard">
        <Typography className="accountCodeLabel">Your Account Code</Typography>
        <Box className="accountCodeRow">
          <Typography className="accountCodeValue">
            {accountCode || "—"}
          </Typography>
          {accountCode && (
            <IconButton
              className={`copyButton${copied ? " copied" : ""}`}
              onClick={handleCopyCode}
              aria-label="Copy account code"
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography className="accountCodeHint">
          Save this code — you&apos;ll need it to log in.
        </Typography>
      </Box>

      <Box className="confirmActions">
        <Button
          component="a"
          href={LOGIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          startIcon={<LoginIcon />}
          className="loginButton"
        >
          Log in to your account
        </Button>
      </Box>

      <Box className="stepsSection">
        <Typography className="stepsTitle">What to Do Next</Typography>

        <Box className="stepsGrid">
          {STEPS.map((step, index) => (
            <Box key={step.title} className="stepCard">
              <Box className="stepNumber">{index + 1}</Box>
              <Box className="stepContent">
                <Typography className="stepTitle">{step.title}</Typography>
                <Typography className="stepDescription">
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="infoBox">
        <InfoOutlinedIcon className="infoIcon" />
        <Typography className="infoText">
          Make sure to save your account details securely. You&apos;ll need
          them to log in and access your account later.
        </Typography>
      </Box>
    </Box>
  );
};

export default FirstStageConfirmTemplate;
